import { PureComponent, ReactNode } from 'react';
import { Input, Select, notification } from 'antd';
import { motion } from 'framer-motion';
import Hotkeys from 'react-hot-keys';
import { GReferWithTrack } from '../../leftPanel/DoctorReferralSearchInput';
import { SpecialityEntity } from '../../../../api/fetchSpecialityForDoctor';
import AntAutoCompleteV2 from './padV2/AntAutoComplete';
import { getDoc } from '../../../../store/idbHandler';
import fetchLatlngFromPincode from '../../../../api/fetchLatlngFromPincode';
import { checkIfValidPhoneNumber } from '../../../../pages/utils';
import AddDoctorWithoutMobileModal from '../../leftPanel/AddDoctorWithoutMobileModal';
import { EkaFocosableElement } from '../../../common/types/EkaFocusable';
import { API_SEARCH } from '../../trackSource';

type Props = {
    doctorData: GReferWithTrack;
    specialities?: SpecialityEntity[] | null;
    onCloseDoctorDialog: () => void;
    onSave: (option: GReferWithTrack) => void;
    isNewReferral?: boolean;
    disabledName?: boolean;
    disabledSpeciality?: boolean;
    disabledClinic?: boolean;
};

type State = {
    docData: GReferWithTrack;
    isAddDoctorWithoutMobileModalOpen: boolean;
};

const CLINIC_ADDRESS = 'clinic-address';

class AddDoctorDialog extends PureComponent<Props, State> {
    REF_MAP: EkaFocosableElement[] = [];

    state: State = {
        docData: this.props.doctorData,
        isAddDoctorWithoutMobileModalOpen: false,
    };

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (
            prevProps.doctorData.referred_doc_profile !== this.props.doctorData.referred_doc_profile
        ) {
            this.setState({ docData: this.props?.doctorData });
        }
    }

    updateDocName = (name: string) => {
        this.setState((prevState) => {
            return {
                ...prevState,
                docData: {
                    ...prevState.docData,
                    name: name,
                    doc: {
                        ...prevState.docData.doc,
                        name: {
                            f: name,
                            l: '',
                        },
                    },
                },
            };
        });
    };

    updateDocNumber = (number: string) => {
        const validNumber = number.replace(/[^0-9]/g, '');
        if (validNumber.length > 10) {
            return;
        }

        this.setState((prevState) => {
            return {
                ...prevState,
                docData: {
                    ...prevState.docData,
                    doc: {
                        ...prevState.docData.doc,
                        phone: {
                            code: '+91',
                            value: validNumber,
                        },
                    },
                },
            };
        });
    };

    onSearch = async (text: string): Promise<string[]> => {
        return new Promise((resolve, _) => {
            resolve(
                this.props?.specialities
                    ?.map((speciality) => speciality.name?.toLowerCase())
                    .filter((t) => t?.toLowerCase()?.includes(text.toLowerCase())) || [],
            );
        });
    };

    onSelect = (specialityName?: string) => {
        const speciality = this.props?.specialities?.find(
            (speciality) => speciality?.name?.toLowerCase() === specialityName?.toLowerCase(),
        );

        if (speciality) {
            this.setState((prevState) => {
                return {
                    docData: {
                        ...prevState.docData,
                        s_id: `${speciality.id}`,
                        s_name: speciality.name,
                        doc: {
                            ...prevState.docData.doc,
                            s_id: `${speciality.id}`,
                            s_name: speciality.name,
                        },
                    },
                };
            });
        }
    };

    setGoogleLocationSuggestion = (autocomplete: google.maps.places.Autocomplete) => {
        const place = autocomplete.getPlace();
        if (!place || !place.geometry || !place.geometry.location) {
            return;
        }
        const lat = place.geometry?.location.lat();
        const lng = place.geometry.location.lng();
        const { name = '', place_id = '', address_components = [] } = place;
        const formatted_address: string[] = [name];
        const additionalInfo: {
            pincode?: string;
            city?: string;
            state?: string;
        } = {};
        address_components?.forEach((addComp) => {
            if (addComp.types.includes('postal_code')) {
                additionalInfo.pincode = addComp.long_name;
            }
            if (addComp.types.includes('locality')) {
                additionalInfo.city = addComp.long_name;
            }
            if (addComp.types.includes('administrative_area_level_1')) {
                additionalInfo.state = addComp.long_name;
            }
            if (addComp.types.includes('sublocality') || addComp.types.includes('route')) {
                formatted_address?.push(addComp.long_name);
            }
        });

        this.setState((prevState) => {
            return {
                docData: {
                    ...prevState.docData,
                    doc: {
                        ...prevState.docData.doc,
                        clinic: {
                            ...(prevState.docData.doc.clinic || {}),
                            landmark: formatted_address?.filter(Boolean).join(', ') || '',
                            city: additionalInfo.city || '',
                            state: additionalInfo.state || '',
                            place_id,
                            pincode: additionalInfo.pincode || '',
                            coordinates: `${lat},${lng}`,
                        },
                    },
                },
            };
        });

        const ele = document.getElementById('line');
        ele?.focus();
    };

    initAutoSuggest = async () => {
        const doc = getDoc();

        const defaultClinic = doc?.profile?.professional?.clinics?.find(
            (clinic) => clinic?._id === doc.profile?.professional?.default_clinic,
        );

        const result = await fetchLatlngFromPincode(defaultClinic?.address?.pin || '');
        const latLng = result.results?.[0]?.geometry?.location;
        const input = document.getElementById(CLINIC_ADDRESS) as HTMLInputElement;

        if (!input) {
            return;
        }

        let defaultBounds;
        if (latLng)
            defaultBounds = {
                north: latLng.lat + 0.2,
                south: latLng.lat - 0.2,
                east: latLng.lng + 0.2,
                west: latLng.lng - 0.2,
            };

        const options = {
            bounds: defaultBounds,
            strictBounds: false,
            componentRestrictions: { country: 'in' },
            types: ['hospital', 'health'],
            fields: [
                'name',
                'geometry.location',
                'place_id',
                'formatted_address',
                'address_components',
            ],
        };
        const autocomplete = new window.google.maps.places.Autocomplete(input, options);
        window.google.maps.event.addListener(autocomplete, 'place_changed', () =>
            this.setGoogleLocationSuggestion(autocomplete),
        );
    };

    componentDidMount(): void {
        const formInputs = document.querySelectorAll('.add-address-form input');
        Array.from(formInputs).forEach((input) => {
            input.addEventListener('blur', () => {
                input.classList.add('border-darwin-status-warning-500');
            });
        });

        if (window.google) {
            this.initAutoSuggest();
        }
    }

    updateDoctorAddress = (landMark: string) => {
        this.setState((prevState) => {
            return {
                docData: {
                    ...prevState.docData,
                    doc: {
                        ...prevState.docData.doc,
                        clinic: {
                            ...(prevState.docData.doc.clinic || {}),
                            landmark: landMark || '',
                        },
                    },
                },
            };
        });
    };

    onSave = () => {
        const { docData } = this.state;

        if (
            docData.doc.phone?.value &&
            docData.doc.phone?.value?.length > 0 &&
            !checkIfValidPhoneNumber(docData.doc.phone.code || '+91', docData.doc.phone?.value)
        ) {
            notification.error({
                message: 'Invalid phone number',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        if (!docData.doc.s_name) {
            notification.error({
                message: 'Speciality not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        if (!docData.doc.name?.f) {
            notification.error({
                message: 'Name not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        if (!docData?.doc?.clinic?.landmark && !docData?.referred_doc_profile?.profile) {
            notification.error({
                message: 'Address not found',
                duration: 5,
                placement: 'topRight',
            });
            return;
        }

        if (!docData.doc.phone?.value) {
            this.setState({ isAddDoctorWithoutMobileModalOpen: true });
            return;
        }

        this.props.onSave(docData);
        this.props.onCloseDoctorDialog();
    };

    closeAddDoctorWithoutMobileModal = () => {
        this.setState({
            isAddDoctorWithoutMobileModalOpen: false,
        });
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number) => {
        this.REF_MAP[elIndex] = ref;
        return;
    };

    handleNextFocus = (elIndex: number) => {
        const { docData } = this.state;

        const elementArr = [
            docData?.name,
            docData?.s_name,
            docData?.doc?.phone?.value,
            docData?.doc?.clinic?.landmark,
        ];
        for (let i = elIndex + 1; i < this.REF_MAP.length; i++) {
            if (!elementArr[i]) {
                this.REF_MAP?.[i]?.focus();
                return;
            }
        }
    };

    onClinicSelect = (cId: string) => {
        const { docData } = this.state;

        const selectedClinic = docData?.referred_doc_profile?.profile?.professional?.clinics?.find(
            (clinic) => clinic._id === cId,
        );

        if (!selectedClinic) {
            return;
        }

        this.setState((prevState) => {
            return {
                docData: {
                    ...prevState.docData,
                    doc: {
                        ...prevState.docData.doc,
                        clinic: {
                            _id: selectedClinic?._id,
                            name: selectedClinic?.name,
                            line: selectedClinic?.address?.line1,
                            landmark: selectedClinic?.address?.line1,
                            city: selectedClinic?.address?.city,
                            state: selectedClinic?.address?.state,
                            city_id: selectedClinic?.city_id,
                            pincode: selectedClinic?.address?.pin,
                            coordinates: selectedClinic?.location?.join(','),
                            gplacesid: selectedClinic?.gplacesid,
                        },
                    },
                },
            };
        });
    };

    render(): ReactNode {
        const { isNewReferral, disabledName, disabledClinic, disabledSpeciality } = this.props;
        const { isAddDoctorWithoutMobileModalOpen, docData } = this.state;

        console.log('referral this.props', this.props);
        console.log('referral this.state', this.state);

        const docName = [docData?.doc?.name?.f, docData?.doc?.name?.l].filter(Boolean).join(' ');
        const speciality = docData?.doc.s_name;
        const docNumber = docData?.doc?.phone?.value;
        const docAddress = docData?.doc?.clinic?.landmark;

        return (
            <>
                <Hotkeys keyName="esc">
                    <motion.div
                        className="w-full select-none overflow-hidden"
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2, ease: 'linear' }}
                    >
                        <div className="flex justify-between w-full items-center py-12 px-24 bg-darwin-primary-100">
                            <div className="space-x-8 flex items-center">
                                <div className="flex items-center justify-center h-26 w-26 bg-darwin-accent-dark-blue-900 rounded-full">
                                    <p className="font-700 text-white text-12">Dr</p>
                                </div>
                                <span className="font-700 text-16">Add doctor</span>
                                {docData.id && (
                                    <svg
                                        className="w-16 h-16"
                                        viewBox="0 0 105 117"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M104.436 33.768C104.144 38.8643 102.1 43.6607 98.4497 47.408L63.4061 83.3816C62.53 84.2809 61.5079 85.1803 60.3398 85.9297C52.3089 91.4757 41.3578 90.4264 34.4951 83.3816C30.2607 79.0348 28.0704 72.8893 28.6545 66.5939C28.8005 62.8467 31.8668 59.9988 35.5172 59.9988C39.3136 59.9988 42.3799 63.1464 42.3799 67.0436C42.3799 69.142 41.5038 70.9407 40.1897 72.2897C40.6277 73.4889 41.3578 74.688 42.3799 75.5873C44.1321 77.386 46.6143 78.4352 49.0966 78.4352C50.7028 78.4352 52.1629 78.1354 53.4771 77.386C54.3531 76.9363 55.0832 76.3368 55.8133 75.5873L73.0431 57.9003L65.8883 50.5557L55.6673 40.0634C53.9151 38.2647 51.4328 37.2155 48.9506 37.2155C46.4683 37.2155 43.9861 38.2647 42.2339 40.0634C41.0658 41.2625 40.1897 42.7614 39.7516 44.2603C41.3578 45.6093 42.5259 47.5579 42.5259 49.8062C42.5259 53.7034 39.4596 56.8511 35.6632 56.8511C32.1589 56.8511 29.0925 54.0032 28.8005 50.4058C27.6324 43.6607 29.6766 36.9157 34.4951 31.9693C38.2915 28.0722 43.402 25.8238 48.9506 25.8238C54.3531 25.8238 59.6097 28.0722 63.4061 31.9693L73.6271 42.4616L80.7819 49.8062L90.7109 39.6137C92.6091 37.6651 93.4852 35.2669 93.4852 32.5689C93.4852 30.1706 92.6091 27.9223 91.0029 26.2735C86.7685 21.477 81.658 17.7298 75.9634 15.0318C69.9768 12.3337 63.6981 10.8348 57.1274 10.8348C44.8622 10.8348 33.473 15.7812 24.7121 24.6247C16.0972 33.4682 11.2787 45.3095 11.2787 57.9003C11.2787 70.4911 16.0972 82.1825 24.7121 91.1759C33.327 100.019 44.8622 104.966 57.1274 104.966C63.6981 104.966 69.9768 103.617 75.9634 100.769C81.658 98.0708 86.7685 94.3236 91.0029 89.5271C94.3613 85.6299 94.2152 79.7842 90.7109 76.037L83.4101 68.5425L91.1489 60.5983L98.4497 68.0928C106.188 76.037 106.481 88.6277 99.1798 97.0216C93.9232 103.017 87.6446 107.664 80.6359 110.961C73.3351 114.409 65.4503 116.058 57.2734 116.058C49.6807 116.058 42.2339 114.559 35.2252 111.561C28.5085 108.563 22.3758 104.366 17.2653 99.12C12.0088 93.724 7.92036 87.5785 5.14608 80.6836C2.22578 73.4889 0.765625 65.8445 0.765625 58.0502C0.765625 50.2559 2.22578 42.6115 5.14608 35.4168C8.06638 28.5219 12.1548 22.2265 17.2653 16.9803C22.5219 11.5843 28.5085 7.38737 35.2252 4.53946C42.2339 1.54166 49.6807 0.042762 57.2734 0.042762C65.4503 0.042762 73.1891 1.84143 80.6359 5.13901C87.7906 8.43659 93.9232 13.0832 99.1798 19.0788C101.37 21.6269 103.122 24.7746 103.852 28.0722C104.29 29.4212 104.436 31.6695 104.436 33.768Z"
                                            fill="#7265E3"
                                        ></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                        <div className="p-16">
                            <div className="flex space-between space-x-16 bg-white w-full">
                                <div className="flex items-center space-x-4 w-704 min-w-210 max-w-704">
                                    <Input
                                        ref={(ref) => {
                                            this.setPropertyInputRef(ref as EkaFocosableElement, 0);
                                        }}
                                        disabled={
                                            disabledName || docData.track?.source === API_SEARCH
                                                ? true
                                                : false
                                        }
                                        value={docName}
                                        placeholder="Doctor Name"
                                        className="rounded-8 px-8"
                                        style={{
                                            background: 'white',
                                            color: 'black',
                                        }}
                                        onChange={(e) => {
                                            this.updateDocName(e.target.value);
                                        }}
                                        onPressEnter={() => {
                                            this.handleNextFocus(0);
                                        }}
                                    />
                                </div>
                                <div className={`w-704 min-w-210 max-w-704`}>
                                    <AntAutoCompleteV2
                                        setRef={(ref) => {
                                            this.setPropertyInputRef(ref, 1);
                                        }}
                                        autoFocus={isNewReferral}
                                        customDropdownClass="capitalize"
                                        placement={'bottomLeft'}
                                        placeholder={'Specialty'}
                                        selectedValue={speciality}
                                        onSearch={this.onSearch}
                                        debounceDelay={200}
                                        getLabelToCompare={(o) => o}
                                        onSelect={(val) => {
                                            this.handleNextFocus(1);
                                            this.onSelect(val);
                                        }}
                                        key={'refer-speciality'}
                                        disabled={
                                            disabledSpeciality ||
                                            docData.track?.source === API_SEARCH
                                        }
                                    />
                                </div>
                                <div className="flex items-center space-x-4 w-704 min-w-210 max-w-704">
                                    <Input
                                        ref={(ref) => {
                                            this.setPropertyInputRef(ref as EkaFocosableElement, 2);
                                        }}
                                        autoFocus={
                                            docData.track?.source === API_SEARCH || !isNewReferral
                                        }
                                        value={docNumber}
                                        placeholder="Mobile number"
                                        onChange={(e) => {
                                            this.updateDocNumber(e.target.value);
                                        }}
                                        onPressEnter={() => {
                                            this.handleNextFocus(2);
                                        }}
                                        className="rounded-8 px-8"
                                        style={{
                                            background: 'white',
                                            color: 'black',
                                        }}
                                    />
                                </div>
                                <div className="min-w-272 max-w-272">
                                    {!!docData?.referred_doc_profile?.profile?.professional?.clinics
                                        ?.length ? (
                                        <Select
                                            style={{ flex: 1, width: '100%' }}
                                            value={docData.doc.clinic?._id}
                                            onChange={this.onClinicSelect}
                                            options={docData?.referred_doc_profile?.profile?.professional?.clinics?.map(
                                                (clinic) => ({
                                                    label: [
                                                        clinic.name,
                                                        clinic?.address?.line1,
                                                        clinic?.address?.pin,
                                                    ].join(', '),
                                                    value: clinic._id,
                                                }),
                                            )}
                                        />
                                    ) : (
                                        <Input
                                            ref={(ref) => {
                                                this.setPropertyInputRef(
                                                    ref as EkaFocosableElement,
                                                    3,
                                                );
                                            }}
                                            disabled={disabledClinic}
                                            value={docAddress}
                                            id={CLINIC_ADDRESS}
                                            placeholder="Clinic name & location"
                                            onChange={(e) => {
                                                this.updateDoctorAddress(e.target.value);
                                            }}
                                            onPressEnter={() => {
                                                this.handleNextFocus(3);
                                            }}
                                            className="rounded-8 px-8"
                                            style={{
                                                background: 'white',
                                                color: 'black',
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end px-16 py-8 bg-darwin-neutral-50">
                            <button
                                className="px-16 py-8"
                                style={{
                                    color: '#004BA8',
                                }}
                                onClick={this.props.onCloseDoctorDialog}
                            >
                                Cancel
                            </button>
                            <button
                                className="px-16 py-8 btn--small primary-btn tooltip-wrapper"
                                onClick={this.onSave}
                            >
                                Add doctor
                            </button>
                        </div>
                    </motion.div>
                    <style>
                        {`
                        .pac-container::after {
                            display: none !important;
                        }
                        `}
                    </style>
                </Hotkeys>
                {isAddDoctorWithoutMobileModalOpen && (
                    <AddDoctorWithoutMobileModal
                        onCancel={this.closeAddDoctorWithoutMobileModal}
                        onConfirm={() => {
                            this.closeAddDoctorWithoutMobileModal();
                            this.props.onSave(docData);
                            this.props.onCloseDoctorDialog();
                        }}
                    />
                )}
            </>
        );
    }
}

export default AddDoctorDialog;
